import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Typography, Box } from '@mui/material';
import { ColorPalette } from 'shared/consts/colorPalette';
import { CategoryTypeEnum } from 'entities/Files/types';
import { useCustomModelStore } from 'widgets/customModel/state';
import { SelectOption } from 'shared/ui/Select/types';

const CustomLabel: FC<{ name: string; description: string }> = ({ name, description }) => {
	return (
		<Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
			<Typography>{name}</Typography>
			<Typography variant="body2" color={ColorPalette.baliHai}>
				{description}
			</Typography>
		</Box>
	);
};

export const useModelsOptions = () => {
	const { t } = useTranslation();
	const { customModels, generativeModels } = useCustomModelStore();

	const optionsForModelSelect: SelectOption[] = useMemo(() => {
		const mappedCustomModels = customModels.map((item) => ({
			value: item._id,
			label: item.name,
			customLabel: <CustomLabel name={item.name} description={t('shared_texts.doc_type_custom')} />,
		}));

		const mappedGenerativeModels = generativeModels.map((item) => ({
			value: item._id,
			label: item.name,
			customLabel: <CustomLabel name={item.name} description={t('shared_texts.doc_type_generative')} />,
		}));
		return [...mappedCustomModels, ...mappedGenerativeModels];
	}, [customModels, generativeModels]);

	const getModelType = (id: string) => {
		const customModel = customModels.find((item) => item._id === id);
		const generativeModel = generativeModels.find((item) => item._id === id);
		return customModel
			? CategoryTypeEnum.custom
			: generativeModel
			? CategoryTypeEnum.generative_model
			: id;
	};

	const getModelName = (id: string) => {
		const customModelName = customModels.find((item) => item._id === id)?.name;
		const generativeModelName = generativeModels.find((item) => item._id === id)?.name;
		return customModelName || generativeModelName || '';
	};

	return { modelsOptions: optionsForModelSelect, getModelType, getModelName };
};
